import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useT } from "@/lib/LanguageContext";
import { PageLoadError } from "./NoAccess";
import { useOperator } from "./OperatorContext";
import { mayOpen } from "./screenAccess";
import { Loader2, CheckCircle2, XCircle, AlertCircle } from "lucide-react";

// An id the server reports that is not listed here is shown under its raw id.
const LABEL_KEYS = {
  stripe: "pset.lblStripe",
  resend: "pset.lblResend",
  anthropic: "pset.lblAnthropic",
  gemini: "pset.lblGemini",
  meta_oauth: "pset.lblMetaOauth",
  linkedin_oauth: "pset.lblLinkedinOauth",
};

const INTEGRATIONS_PATH = "/super-admin/integrations";
const TEAM_PATH = "/super-admin/team";

/**
 * Read-only: every value here is owned by the server's configuration (super-admin-s1-shell
 * contract 1), so the page makes one request, GET /super-admin/settings, and offers no form.
 *   data.integrations[id] = { configured: bool, demo_mode: bool, env_keys: string[], from_email?, key_prefix?,
 *                             tenant_mail_domain?: string ("" = practices cannot send),
 *                             tenant_mail_domain_env_keys?: string[] }
 *   data.platform = { app_url: string, default_tenant: string, super_admin_domains: string[],
 *                     env_keys?: { app_url, default_tenant, super_admin_domains } }
 */
export default function PlatformSettings() {
  const t = useT("superadmin");
  const operator = useOperator();
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    setLoadError(null);
    api.get("/super-admin/settings").then((r) => setData(r.data)).catch(setLoadError);
  }, [attempt]);

  if (loadError) return <PageLoadError error={loadError} onRetry={() => setAttempt((n) => n + 1)} testId="platform-settings-load-error" />;
  if (!data) return <Loader2 className="w-6 h-6 animate-spin text-amber-600" />;

  const platform = data.platform || {};
  const envKeys = platform.env_keys || {};
  const domains = platform.super_admin_domains || [];
  const canOpenIntegrations = mayOpen(INTEGRATIONS_PATH, operator);
  const canOpenTeam = mayOpen(TEAM_PATH, operator);

  // One system-info row: label, the value, and the variable the server reads it from.
  const infoRow = (field, label, value, testId, last) => (
    <div className={`flex items-start justify-between gap-6 py-3 ${last ? "" : "border-b border-slate-100"}`}>
      <div>
        <div className="text-slate-500">{label}</div>
        {envKeys[field] && <code className="text-[11px] text-slate-400 font-mono">{envKeys[field]}</code>}
      </div>
      <div className="flex flex-wrap justify-end gap-2" data-testid={testId}>{value}</div>
    </div>
  );

  return (
    <div data-testid="sa-settings">
      <h1 className="font-sans font-semibold text-5xl mb-2 text-slate-800">{t("pset.title")}</h1>
      <p className="text-slate-500 mb-8">{t("pset.subtitle")}</p>

      <div className="rounded-2xl bg-white border border-slate-200 p-8 mb-6">
        <h3 className="font-sol font-semibold text-xl mb-6">{t("pset.integrations")}</h3>
        <div className="space-y-3">
          {Object.entries(data.integrations || {}).map(([key, val]) => {
            const ok = val.configured;
            const demo = val.demo_mode;
            return (
              <div key={key} data-testid={`pset-integ-${key}`} className="flex items-start gap-4 p-4 rounded-xl bg-white border border-slate-100">
                <div className="mt-0.5">
                  {ok && !demo ? <CheckCircle2 className="w-5 h-5 text-emerald-400" /> :
                   demo ? <AlertCircle className="w-5 h-5 text-amber-600" /> :
                   <XCircle className="w-5 h-5 text-rose-400" />}
                </div>
                <div className="flex-1">
                  <div className="font-medium">{LABEL_KEYS[key] ? t(LABEL_KEYS[key]) : key}</div>
                  {/* The status sentence (and its link) is one line; each fact about the
                      integration (sender, key prefix, practice mail domain) is a line of its own,
                      so a link never runs into the label that follows it. */}
                  <div className="text-xs text-slate-500 mt-1" data-testid={`pset-status-${key}`}>
                    {ok && !demo && t("pset.configuredActive")}
                    {demo && t("pset.demoActive")}
                    {!ok && !demo && t("pset.notConfigured", { keys: (val.env_keys || []).join(" + ") })}
                    {!ok && !demo && canOpenIntegrations && (
                      <> <Link to={INTEGRATIONS_PATH} className="underline text-amber-700">{t("pset.openIntegrations")}</Link></>
                    )}
                  </div>
                  {val.from_email && (
                    <div className="text-xs text-slate-500 mt-1" data-testid={`pset-from-email-${key}`}>
                      {t("pset.fromEmail", { email: val.from_email })}
                    </div>
                  )}
                  {val.key_prefix && (
                    <div className="text-xs text-slate-500 mt-1" data-testid={`pset-key-prefix-${key}`}>
                      {t("pset.keyPrefix", { prefix: val.key_prefix })}
                    </div>
                  )}
                  {"tenant_mail_domain" in val && (
                    <div data-testid="pset-tenant-mail-domain"
                         className={`text-xs mt-1 ${val.tenant_mail_domain ? "text-slate-500" : "text-rose-500"}`}>
                      {val.tenant_mail_domain
                        ? t("pset.tenantMailDomain", { domain: val.tenant_mail_domain })
                        : t("pset.tenantMailDomainNone", { keys: (val.tenant_mail_domain_env_keys || []).join(" / ") })}
                    </div>
                  )}
                </div>
                <div className="text-xs uppercase tracking-wider font-semibold" data-testid={`pset-badge-${key}`}>
                  {ok && !demo && <span className="text-emerald-400">{t("pset.badgeOk")}</span>}
                  {demo && <span className="text-amber-600">{t("pset.badgeDemo")}</span>}
                  {!ok && !demo && <span className="text-rose-400">{t("pset.badgeOff")}</span>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="rounded-2xl bg-white border border-slate-200 p-8">
        <h3 className="font-sol font-semibold text-xl mb-2">{t("pset.systemInfo")}</h3>
        <p className="text-xs text-slate-500 mb-4" data-testid="pset-server-managed">{t("pset.serverManaged")}</p>
        {canOpenTeam && (
          <p className="text-xs mb-4">
            <Link to={TEAM_PATH} className="underline text-amber-700" data-testid="pset-team-link">{t("pset.teamAccessNote")}</Link>
          </p>
        )}
        <div className="text-sm">
          {infoRow("app_url", t("pset.publicUrl"),
            <code className="text-amber-600 text-xs break-all" data-testid="pset-app-url">{platform.app_url || "—"}</code>, "pset-app-url-row")}
          {infoRow("default_tenant", t("pset.defaultTenantSystem"),
            <code className="text-amber-600 text-xs">{platform.default_tenant}</code>, "pset-default-tenant")}
          {infoRow("super_admin_domains", t("pset.saDomainsSystem"),
            domains.length === 0
              ? <span className="text-xs text-slate-500">{t("pset.saDomainsNone")}</span>
              : domains.map((d) => <code key={d} className="text-amber-600 text-xs break-all">@{d}</code>),
            "pset-domains", true)}
        </div>
      </div>
    </div>
  );
}
